import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { Breadcrumbs } from 'src/app/shared/models/breadcrumbs';
import { SupplierException } from 'src/app/shared/models/supplier-exception';
import { PlantMaster } from 'src/app/shared/models/plant-master';
import { SupplierProfile } from 'src/app/shared/models/supplier-profile';
import { DataService } from 'src/app/shared/services/data-service/data.service';
import { StoreService } from 'src/app/shared/services/store-service/store.service';
import { UserService } from 'src/app/shared/services/user-service/user.service';

@Component({
  standalone: false,
  selector: 'app-create-supplier-profile',
  templateUrl: './create-supplier-profile.component.html',
  styleUrls: ['./create-supplier-profile.component.scss'],
})
export class CreateSupplierProfileComponent implements OnInit {
  supplierIdentity: string;
  supplierName: string;
  plant: string;
  vendor: string;
  isLoading: boolean = false;
  validationError: { [key: string]: string } = {
    plant: '',
    vendor: '',
    supplierIdentity: '',
  };

  breadcrumbItems: Breadcrumbs[];

  constructor(
    public storeService: StoreService,
    private userService: UserService,
    private dataService: DataService,
    private router: Router
  ) {}

  async ngOnInit(): Promise<void> {
    if (!(await this.userService.getUserDetail()).canMaintainSupplierProfile) {
      this.router.navigate(['/']);
    }
    this.buildBreadcrumbs();
  }

  buildBreadcrumbs() {
    this.breadcrumbItems = [
      {
        text: 'Supplier Profile',
        link: '/supplier-profile',
      },
      {
        text: 'Create New Supplier Profile',
        link: '/supplier-profile/create-supplier-profile',
      },
    ];
  }

  clearValidation() {
    this.validationError = {
      plant: '',
      vendor: '',
      supplierIdentity: '',
    };
  }

  checkDuplicatePlantAndVendor(): boolean {
    let supplier = this.supplierIdentity;
    let plant = this.plant;
    let vendor = this.vendor;
    if (
      this.storeService.supplierProfiles.filter(function (item) {
        return (
          item.plantCode === plant &&
          item.vendCode === vendor &&
          item.supplierIdentity === supplier
        );
      }).length > 0
    ) {
      return true;
    }
    return false;
  }

  checkValidation(exceptionsToSave: SupplierException[]) {
    this.clearValidation();
    this.validationError = {
      plant:
        this.plant && this.plant.length == 4
          ? ''
          : 'Plant code must have exactly 4 characters',
      vendor:
        this.vendor && this.vendor.length <= 10
          ? ''
          : 'Vendor code can have maximum 10 characters',
      supplierIdentity:
        this.supplierIdentity && this.supplierIdentity.length > 0
          ? ''
          : 'Supplier Identity cannot be empty',
    };
    // Check Vendor and Plant existing in other card
    if (this.checkDuplicatePlantAndVendor()) {
      this.validationError['supplierIdentity'] = 'Supplier existing';
      this.validationError['vendor'] = 'Vendor existing';
      this.validationError['plant'] = 'Plant existing';
    }

    // Check existing in option list
    if (
      this.validationError['plant'] === '' &&
      this.storeService.plants.filter((data) => {
        return data.t001W_WERKS_Plant === this.plant;
      }).length == 0
    ) {
      this.validationError['plant'] = 'Plant not existing';
    }

    if (
      this.validationError['vendor'] === '' &&
      this.storeService.vendors.filter((data) => {
        return data.vendor_Number === this.vendor;
      }).length == 0
    ) {
      this.validationError['vendor'] = 'Vendor not existing';
    }

    return Object.keys(this.validationError)
      .map((key) => {
        return !Boolean(this.validationError[key]);
      })
      .every((i) => i);
  }

  async handleSaveException(exceptionsToSave: SupplierException[]) {
    if (this.checkValidation(exceptionsToSave)) {
      this.isLoading = true;
      // Get current user
      let currentUser = (await this.userService.getUserDetail()).name;

      // Filter out new exception
      exceptionsToSave = exceptionsToSave.filter(
        (data: SupplierException) => data.criteria != 'New'
      );

      // Prepare supplier profile
      let supplierProfile = <SupplierProfile>{
        supplierIdentity: this.supplierIdentity,
        supplierName: this.supplierName,
        plantCode: this.plant,
        vendCode: this.vendor,
        exceptions: exceptionsToSave,
        createdBy: currentUser,
        lastChangedBy: currentUser,
      };

      // Call API to create
      let result = await this.dataService.createSupplierProfile(
        supplierProfile
      );

      // Create Success, navigate to that
      if (result) {
        this.storeService.addSupplierProfile(supplierProfile);
        this.router.navigate(['/supplier-profile', this.supplierIdentity]);
        this.isLoading = false;
      }
    }
  }
}

